import React, { useState } from 'react';
import { User, Activity, Shield, Target, Award } from 'lucide-react';

const Onboarding = ({ onComplete }) => {
    const [step, setStep] = useState(1);
    const [data, setData] = useState({
        role: '',
        hand: '',
        battingStyle: '',
        battingPosition: '',
        bowlingArm: '',
        bowlingType: '',
        level: '',
        inventory: [],
        habitStack: []
    });

    const handleNext = () => {
        if (step === 4) {
            onComplete(data);
        } else {
            setStep(step + 1);
        }
    };

    const updateData = (key, value) => {
        setData({ ...data, [key]: value });
    };

    // --- Step 1: Role Selection ---
    const renderRoleSelection = () => (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white mb-6">Who are you?</h2>
            <div className="grid grid-cols-2 gap-4">
                {[
                    { id: 'Batter', icon: Shield, label: 'Batter' },
                    { id: 'Bowler', icon: Target, label: 'Bowler' },
                    { id: 'Batting All-Rounder', icon: Activity, label: 'Batting All-Rounder' },
                    { id: 'Bowling All-Rounder', icon: Activity, label: 'Bowling All-Rounder' },
                    { id: 'Wicket-Keeper', icon: User, label: 'Wicket-Keeper' }
                ].map((role) => (
                    <button
                        key={role.id}
                        onClick={() => updateData('role', role.id)}
                        className={`p-4 rounded-xl border-2 flex flex-col items-center justify-center gap-3 transition-all ${data.role === role.id
                            ? 'border-purple-500 bg-purple-500/20 text-white'
                            : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                            }`}
                    >
                        <role.icon size={32} />
                        <span className="font-bold text-sm">{role.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );

    // --- Step 2: Attributes ---
    const renderAttributes = () => {
        const isBatter = data.role.includes('Batter') || data.role.includes('Keeper');
        const isBowler = data.role.includes('Bowler');
        const isAllRounder = data.role.includes('All-Rounder');

        return (
            <div className="space-y-6">
                <h2 className="text-2xl font-bold text-white mb-6">What's your style?</h2>

                {(isBatter || isAllRounder || data.role === 'Wicket-Keeper') && (
                    <div className="space-y-4 animate-fadeIn">
                        <div>
                            <label className="block text-gray-400 text-sm mb-2">Batting Hand</label>
                            <div className="flex gap-2">
                                {['Right-Hand', 'Left-Hand'].map(opt => (
                                    <button
                                        key={opt}
                                        onClick={() => updateData('hand', opt)}
                                        className={`flex-1 p-3 rounded-lg border ${data.hand === opt ? 'bg-purple-600 border-purple-600 text-white' : 'border-gray-700 text-gray-400'}`}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-gray-400 text-sm mb-2">Batting Style</label>
                            <select
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white"
                                onChange={(e) => updateData('battingStyle', e.target.value)}
                                value={data.battingStyle}
                            >
                                <option value="">Select Style...</option>
                                <option value="Defensive">Defensive (Anchor)</option>
                                <option value="Aggressive">Aggressive (Power)</option>
                                <option value="Classic">Classic (Technical)</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-gray-400 text-sm mb-2">Preferred Position</label>
                            <div className="flex gap-2 text-xs">
                                {['Top (1-3)', 'Middle (4-7)', 'Tail Ender'].map(opt => (
                                    <button
                                        key={opt}
                                        onClick={() => updateData('battingPosition', opt)}
                                        className={`flex-1 p-3 rounded-lg border ${data.battingPosition === opt ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-700 text-gray-400'}`}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {(isBowler || isAllRounder) && (
                    <div className="space-y-4 animate-fadeIn pt-4 border-t border-gray-800">
                        <div>
                            <label className="block text-gray-400 text-sm mb-2">Bowling Arm</label>
                            <div className="flex gap-2">
                                {['Right-Arm', 'Left-Arm'].map(opt => (
                                    <button
                                        key={opt}
                                        onClick={() => updateData('bowlingArm', opt)}
                                        className={`flex-1 p-3 rounded-lg border ${data.bowlingArm === opt ? 'bg-purple-600 border-purple-600 text-white' : 'border-gray-700 text-gray-400'}`}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-gray-400 text-sm mb-2">Bowling Type</label>
                            <select
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white"
                                onChange={(e) => updateData('bowlingType', e.target.value)}
                                value={data.bowlingType}
                            >
                                <option value="">Select Type...</option>
                                <option value="Fast">Fast</option>
                                <option value="Fast-Medium">Fast-Medium</option>
                                <option value="Medium-Swing">Medium (Swing)</option>
                                <option value="Off-Spin">Off-Spin</option>
                                <option value="Leg-Spin">Leg-Spin</option>
                                <option value="Slow-Left-Arm">Slow Left Arm (Orthodox)</option>
                                <option value="Chinaman">Chinaman</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // --- Step 3: Environment Check ---
    const toggleSelection = (key, item) => {
        const current = data[key];
        if (current.includes(item)) {
            updateData(key, current.filter(i => i !== item));
        } else {
            updateData(key, [...current, item]);
        }
    };

    const renderEnvironment = () => (
        <div className="space-y-8 animate-fadeIn">
            <div>
                <h2 className="text-2xl font-bold text-white mb-4">Your Setup</h2>
                <p className="text-gray-400 text-sm mb-4">What do you have access to?</p>
                <div className="grid grid-cols-2 gap-3">
                    {['Partner', 'Net Access', 'Gym Access', 'VR Headset', 'Bowling Machine'].map(item => (
                        <button
                            key={item}
                            onClick={() => toggleSelection('inventory', item)}
                            className={`p-3 rounded-lg border text-sm text-center transition-all ${data.inventory.includes(item)
                                ? 'bg-blue-600/20 border-blue-500 text-blue-400 font-bold'
                                : 'bg-gray-800 border-gray-700 text-gray-500 hover:border-gray-600'
                                }`}
                        >
                            {item}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <h2 className="text-2xl font-bold text-white mb-4">Your Habits</h2>
                <p className="text-gray-400 text-sm mb-4">How do you usually train?</p>
                <div className="flex flex-wrap gap-2">
                    {['Matches', 'Nets', 'Fitness', 'VR Training', 'Home Drills'].map(item => (
                        <button
                            key={item}
                            onClick={() => toggleSelection('habitStack', item)}
                            className={`px-4 py-2 rounded-full border text-sm transition-all ${data.habitStack.includes(item)
                                ? 'bg-purple-600 border-purple-600 text-white'
                                : 'bg-transparent border-gray-600 text-gray-400'
                                }`}
                        >
                            {item}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    // --- Step 3: Calibration ---
    const renderCalibration = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <Award size={48} className="text-yellow-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white">What's your level?</h2>
                <p className="text-gray-400 text-sm">We use this to calibrate your drills.</p>
            </div>

            <div className="space-y-3">
                {[
                    { val: 'Beginner', desc: 'Gully / Park Cricket' },
                    { val: 'Amateur', desc: 'Club / Weekend League' },
                    { val: 'Semi-Pro', desc: 'District / Grade Cricket' }
                ].map((level) => (
                    <button
                        key={level.val}
                        onClick={() => updateData('level', level.val)}
                        className={`w-full p-4 rounded-xl border text-left flex justify-between items-center ${data.level === level.val
                            ? 'bg-gradient-to-r from-yellow-600/20 to-transparent border-yellow-500 text-white'
                            : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                            }`}
                    >
                        <div>
                            <span className="font-bold block">{level.val}</span>
                            <span className="text-xs opacity-70">{level.desc}</span>
                        </div>
                        {data.level === level.val && <Award size={20} className="text-yellow-500" />}
                    </button>
                ))}
            </div>
        </div>
    );

    const canProceed = () => {
        if (step === 1) return !!data.role;
        if (step === 2) {
            // Basic validation based on role
            const hasBatting = data.role.includes('Batter') || data.role.includes('Keep') || data.role.includes('All');
            const hasBowling = data.role.includes('Bowler') || data.role.includes('All');

            if (hasBatting && (!data.hand || !data.battingStyle || !data.battingPosition)) return false;
            if (hasBowling && (!data.bowlingArm || !data.bowlingType)) return false;
            return true;
        }
        if (step === 3) return true; // Environment is optional
        if (step === 4) return !!data.level;
        return false;
    };

    return (
        <div className="h-full bg-black flex flex-col p-6">
            {/* Progress Bar */}
            <div className="flex gap-2 mb-8">
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? 'bg-purple-500' : 'bg-gray-800'}`} />
                ))}
            </div>

            <div className="flex-1 overflow-y-auto">
                {step === 1 && renderRoleSelection()}
                {step === 2 && renderAttributes()}
                {step === 3 && renderEnvironment()}
                {step === 4 && renderCalibration()}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-800">
                <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className="w-full py-4 bg-white text-black font-bold rounded-xl disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                >
                    {step === 4 ? 'Build Player Card' : 'Continue'}
                </button>
            </div>
        </div>
    );
};

export default Onboarding;
